import {
  hyperlinkParagraph,
  paragraph,
  richTextDocument,
  unorderedList,
} from "./richText";

export const DEFENDR_HANDLE = "aspiro-defendr-door-system";

const currentHeroImage =
  "https://cdn.bfldr.com/QL33D4U/at/x5bnc9rg97msv8m48hhc5vg9/Defendr_Door_Hallway_page05.jpg?auto=webp";
const warmHeroThumb =
  "https://thumbs.bfldr.com/at/cxxt4tcsnzs83fb99cc5xj44/v/1323530863?fit=bounds&height=1200&width=1800";
const systemDoorImage =
  "https://cdn.bfldr.com/QL33D4U/at/m5p45x3k9kg3rj3kvmk7ckbq/Defendr-System-Intro.jpg?auto=webp";

const sharedApplicationImage = currentHeroImage;
const sharedCaseStudyImage = currentHeroImage;
const sharedTestingImage = systemDoorImage;

export const DEFENDR_AWARDS = [
  {
    slug: "architectural-products",
    eyebrow: "Architectural Products",
    title: "2025 Product Innovation Award",
    description:
      "Celebrating forward-thinking design that sets a new standard for architectural doors.",
    href: "https://brandfolder.com/s/sb6b6gtv6rtwpwfkv83wq3g3",
  },
  {
    slug: "campus-security-today",
    eyebrow: "Campus Security Today",
    title: "2025 Secure Campus Award",
    description:
      "Honoring solutions that protect students, faculty, and staff in educational environments.",
    href: "https://brandfolder.com/s/sb6b6gtv6rtwpwfkv83wq3g3",
  },
  {
    slug: "security-today",
    eyebrow: "Security Today",
    title: "2025 Product of the Year",
    description:
      "Recognized for outstanding contribution to security technology and product excellence.",
    href: "https://brandfolder.com/s/sb6b6gtv6rtwpwfkv83wq3g3",
  },
];

export const DEFENDR_CONSTRUCTION_SPECIFICATIONS = {
  title: "Construction Specifications",
  eyebrow: "A System That Deters and Delays Attackers",
  description:
    "Use a full opening strategy to delay entry attempts while preserving visibility, code compliance, and a school-friendly appearance.",
  image: systemDoorImage,
  hotspots: [
    {
      number: 1,
      id: "door-core",
      title: "Solid Core Wood Door",
      description:
        "Constructed with an extra heavy-duty particleboard or structural composite lumber core. Available in non-rated, 20- and 45-minute fire rated options.",
      x: 40,
      y: 22,
    },
    {
      number: 2,
      id: "attack-resistant-glazing",
      title: "Glass & Glazing",
      description:
        "Security glass, tested using ASTM F3561 or Shooter Attack Test method FTD-SA. Factory glazing required.",
      x: 45,
      y: 33,
    },
    {
      number: 3,
      id: "hardware",
      title: "Hardware Options",
      description:
        "ANSI/BHMA Grade 1 hardware approved to meet ASTM F3561 or the Shooter Attack Test Method FTD-SA requirements.",
      x: 39,
      y: 50,
    },
    {
      number: 4,
      id: "metal-vision-kit",
      title: "Metal Vision Frame",
      description: "18-gauge steel single and dual pane design options.",
      x: 51,
      y: 55,
    },
    {
      number: 5,
      id: "metal-frame",
      title: "Door Frame",
      description:
        "14- or 16-gauge hollow metal frame, 3-piece knocked down or welded.",
      x: 32,
      y: 75,
    },
  ],
  links: [
    {
      title: "Cores",
      description:
        "Choose from tested cores built for maximum durability, safety, and performance.",
      href: "/cores/attack-resistant",
    },
    {
      title: "Glazing",
      description:
        "Attack-resistant glazing options for safety, visibility, and performance.",
      href: "/categories/aspiro-defendr-door-system",
    },
    {
      title: "Hardware",
      description: "Hardware built for security, function, durability.",
      href: "/technical-resources",
    },
    {
      title: "Finishing",
      description: "Finishes for protection, style, and longevity.",
      href: "/finishes",
    },
  ],
};

export const DEFENDR_DRAFT_CONTENT = {
  title: "Aspiro® Series Defendr Door System™",
  pageType: "categories",
  hero: {
    title: "Aspiro® Series Defendr Door System™",
    description:
      "Every component of our attack-resistant interior door system works to protect students and staff inside the classroom.",
    heroImage: warmHeroThumb,
    heroImageFallback: currentHeroImage,
    heroImagePosition: "center",
  },
  awards: {
    title: "Recognized for Innovation, Security, and Excellence",
    paragraphs: [
      "The Aspiro® Series Defendr Door System™ has earned top industry accolades for its advanced design and unmatched performance.",
      "These awards reflect our commitment to safety, durability, and innovation—ensuring every opening delivers peace of mind.",
    ],
  },
  intro: {
    title: "Stronger Together",
    videoHref: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
    videoPoster: currentHeroImage,
    videoLabel: "Watch the Defendr overview video",
    paragraphs: [
      "Learn from the past to create a safer future",
      "In recent decades, we have seen how increasing mental health issues and escalating violence can create a threatening school environment. We believe every student and staff member should feel safe at school. Partnering with security experts, we developed the Aspiro® Series Defendr Door System™, a practical solution that adds layers of protection to slow down unwanted entry—creating safer spaces inside schools and classrooms.",
      "Proven performance and award-winning innovation",
      "The Aspiro® Series Defendr Door System™ is tested to the latest industry standards for forced entry. The system has been recognized as the first to meet ASTM F3561 certification and has received multiple architectural and security awards.",
      "Practical protection designed for real schools",
      "While adding protection to delay and defend, we also believe security should be invisible. The Aspiro® Series Defendr Door System™ is a one-time, affordable solution that is a balance of defense, protection, and style. Attack-resistant security glass lets in natural light, while sustainable materials and laminate, premium painted, or select wood veneer faces offer a comforting and attractive appearance.",
      "As an added benefit, the Aspiro® Series Defendr Door System™ creates quieter spaces with sound-absorbing, solid core construction, while also meeting code and safety requirements with a selection of fire-rated doors.",
    ],
  },
  applications: [
    {
      title: "Classrooms",
      description:
        "Help create a safer learning environment by providing attack resistance at the primary point of entry while maintaining code compliance and daily functionality.",
      image: sharedApplicationImage,
    },
    {
      title: "Special Needs Rooms",
      description:
        "Enhanced protection supports vulnerable occupants without compromising accessibility, visibility, or a supportive learning atmosphere.",
      image: sharedApplicationImage,
    },
    {
      title: "Gymnasium",
      description:
        "Large, high-traffic spaces benefit from durable reinforced assemblies during school hours and after-hours events.",
      image: sharedApplicationImage,
    },
    {
      title: "Library",
      description:
        "Attack-resistant assemblies help safeguard students and staff in open, collaborative environments while maintaining design continuity.",
      image: sharedApplicationImage,
    },
    {
      title: "Media Room",
      description:
        "Integrated security supports technology-rich spaces that often serve multiple functions while balancing visibility and protection.",
      image: sharedApplicationImage,
    },
    {
      title: "Administrative Offices",
      description:
        "Front-office and administrative areas gain an added layer of defense at key control points for staff safety and secure visitor management.",
      image: sharedApplicationImage,
    },
    {
      title: "Stairwells",
      description:
        "Reinforce critical vertical circulation paths with verified attack resistance at key egress points while maintaining life safety.",
      image: sharedApplicationImage,
    },
  ],
  securityTiers: {
    title: "Understanding Three Security Tiers",
    paragraphs: [
      "Not all security doors are evaluated the same way. To simplify specification and clarify performance expectations, our portfolio is organized into three distinct security designations.",
      "Each tier represents a complete opening solution — door, frame, hardware, and glazing — engineered to work together at a specific level of performance. Rather than sorting through individual components, you can now select the tier that aligns with your project’s protection goals.",
    ],
    bullets: [
      "Make performance expectations easier to understand",
      "Align solutions with recognized forced-entry standards such as ASTM F3561 and FTD-SA",
      "Reduce specification errors by grouping compatible components",
      "Simplify ordering and documentation",
      "Provide scalable security solutions for a range of applications",
    ],
    chartHref: "https://brandfolder.com/s/xqrnq3rphk3qr4ttjw3hkbv6",
  },
  testing: {
    title: "Testing",
    image: sharedTestingImage,
    paragraphs: [
      "FTD-SA testing was developed to reflect lessons learned from acts of school violence and to evaluate the full opening as a system instead of isolated parts.",
      "Aspiro® Series Defendr Door System™ testing focuses on delaying access long enough to improve response time, preserve safe egress, and maintain classroom visibility under real-world threat scenarios.",
    ],
  },
  resources: {
    title: "Resources for Architects & School Districts",
    sections: [
      {
        title: "Literature",
        items: [
          {
            title: "Defendr Brochure",
            href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
          },
          {
            title: "School Security eBook",
            href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
          },
        ],
      },
      {
        title: "Specification Tools",
        items: [
          {
            title: "Attack Resistant Wood Doors 3-Part Spec",
            href: "https://cdn.bfldr.com/QL33D4U/at/6zcjwf2tt8wmw937k553j5gn/2026-FOS-SPC-101-0313_SECTION_081416_Attack_Resistant_Wood_Doors.docx",
          },
          {
            title: "Aspiro Defendr Specifications",
            href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
          },
          {
            title: "Aspiro Series Defendr Door Guide",
            href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
          },
        ],
      },
      {
        title: "Technical Documents",
        items: [
          {
            title: "DefendEd™ Fire-Rated Attack-Resistant Glass",
            href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
          },
          {
            title: "DefendEd™ Non-Rated Attack-Resistant Glass",
            href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
          },
          {
            title: "School Guard Glass™ SGG-L7-SG4",
            href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
          },
          {
            title: "School Guard Glass™ SGG-L7-SG5",
            href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
          },
        ],
      },
      {
        title: "Frequently Asked Questions",
        items: [
          {
            title: "Aspiro Series Defendr Door System FAQ",
            href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
          },
        ],
      },
      {
        title: "Quote & Order Forms",
        items: [
          {
            title: "Request a Defendr Quote",
            href: "/contact-us",
          },
        ],
      },
      {
        title: "Warranty",
        items: [
          {
            title: "Defendr Warranty Information",
            href: "/technical-resources",
          },
        ],
      },
    ],
  },
  caseStudies: [
    {
      title: "Defendr Case Study 102",
      description:
        "See how an education project team applied attack-resistant openings in a real school environment.",
      image: sharedCaseStudyImage,
      href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
    },
    {
      title: "Defendr Case Study 103",
      description:
        "Review another school-security project example focused on practical protection and day-to-day functionality.",
      image: sharedCaseStudyImage,
      href: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
    },
  ],
  ctaForm: {
    title: "Download the School Security eBook",
    image: currentHeroImage,
    paragraphs: [
      "Get the Defendr eBook and connect with our team to learn more about attack-resistant door systems for education environments.",
    ],
    anchor: "contact-us",
    formId: "fb12f8fd-3e9a-f011-b4cc-000d3a5a7d0d",
    ebookDownloadUrl: "https://brandfolder.com/s/hw265v38bbs44mfqt9jsff3v",
    ebookFileName: "Defendr-School-Security-Ebook.pdf",
    successTitle: "Your Defendr resources are ready",
    successBody:
      "The Defendr team can help you evaluate classroom openings, compare security tiers, and specify the right opening strategy for your next project.",
  },
};

const createTextBlock = ({
  id,
  title,
  anchor,
  content,
  showTitle = true,
}: {
  id: string;
  title: string;
  anchor: string;
  content: ReturnType<typeof richTextDocument>;
  showTitle?: boolean;
}) => ({
  __typename: "Block",
  sys: { id },
  title,
  showTitle,
  layout: "Text",
  anchor,
  padding: "Top and Bottom",
  backgroundColor: "Default",
  text: { json: content },
});

export const buildDefendrMockPage = () => {
  const awardsBlock = createTextBlock({
    id: "defendr-awards-block",
    title: DEFENDR_DRAFT_CONTENT.awards.title,
    anchor: "awards",
    content: richTextDocument(
      ...DEFENDR_DRAFT_CONTENT.awards.paragraphs.map((copy) => paragraph(copy)),
    ),
  });

  const introBlock = {
    __typename: "Block",
    sys: { id: "defendr-intro-block" },
    layout: "Split Block",
    padding: "Top and Bottom",
    backgroundColor: "Default",
    anchor: "intro",
    leftBlock: {
      title: "Video Overview",
      showTitle: false,
      image: [{ cdn_url: DEFENDR_DRAFT_CONTENT.intro.videoPoster }],
      imageCover: true,
      ctAsCollection: {
        items: [
          {
            sys: { id: "defendr-intro-video-cta" },
            title: "Watch Video",
            link: DEFENDR_DRAFT_CONTENT.intro.videoHref,
          },
        ],
      },
    },
    rightBlock: {
      title: DEFENDR_DRAFT_CONTENT.intro.title,
      showTitle: true,
      text: {
        json: richTextDocument(
          ...DEFENDR_DRAFT_CONTENT.intro.paragraphs.map((copy) =>
            paragraph(copy),
          ),
        ),
      },
      ctAsCollection: { items: [] },
    },
  };

  const applicationsBlock = {
    __typename: "Block",
    sys: { id: "defendr-applications-block" },
    title: "Applications",
    showTitle: true,
    layout: "Carousel",
    carouselType: "Card",
    padding: "Top and Bottom",
    anchor: "applications",
    carouselCollection: {
      items: DEFENDR_DRAFT_CONTENT.applications.map((item) => ({
        sys: {
          id: `application-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        },
        title: item.title,
        image: [{ cdn_url: item.image }],
        imageCover: true,
        cardType: "Default",
        description: { json: richTextDocument(paragraph(item.description)) },
        link: "/categories/aspiro-defendr-door-system",
        linkText: "Learn More",
      })),
    },
    ctAsCollection: { items: [] },
  };

  const constructionBlock = {
    __typename: "Block",
    sys: { id: "defendr-construction-block" },
    title: DEFENDR_CONSTRUCTION_SPECIFICATIONS.title,
    showTitle: true,
    layout: "Feature",
    feature: "Construction Specifications",
    padding: "Top and Bottom",
    backgroundColor: "Light Gray",
    anchor: "construction",
  };

  const tiersBlock = {
    __typename: "Block",
    sys: { id: "defendr-security-tiers-block" },
    layout: "Split Block",
    padding: "Top and Bottom",
    backgroundColor: "Default",
    leftBlock: {
      title: DEFENDR_DRAFT_CONTENT.securityTiers.title,
      showTitle: true,
      text: {
        json: richTextDocument(
          ...DEFENDR_DRAFT_CONTENT.securityTiers.paragraphs.map((copy) =>
            paragraph(copy),
          ),
          unorderedList(DEFENDR_DRAFT_CONTENT.securityTiers.bullets),
          hyperlinkParagraph(
            "View the security tier chart",
            DEFENDR_DRAFT_CONTENT.securityTiers.chartHref,
          ),
        ),
      },
      ctAsCollection: { items: [] },
    },
    rightBlock: {
      title: "Security Tier Reference",
      showTitle: false,
      image: [{ cdn_url: currentHeroImage }],
      imageCover: true,
      text: {
        json: richTextDocument(
          paragraph(
            "Use the chart to align opening performance expectations with the level of school security each project requires.",
          ),
        ),
      },
      ctAsCollection: {
        items: [
          {
            sys: { id: "defendr-security-chart" },
            title: "View Tier Chart",
            link: DEFENDR_DRAFT_CONTENT.securityTiers.chartHref,
          },
        ],
      },
    },
  };

  const testingBlock = {
    __typename: "Block",
    sys: { id: "defendr-testing-block" },
    layout: "Split Block",
    padding: "Top and Bottom",
    backgroundColor: "Gray",
    leftBlock: {
      title: DEFENDR_DRAFT_CONTENT.testing.title,
      showTitle: true,
      text: {
        json: richTextDocument(
          ...DEFENDR_DRAFT_CONTENT.testing.paragraphs.map((copy) =>
            paragraph(copy),
          ),
        ),
      },
      ctAsCollection: { items: [] },
    },
    rightBlock: {
      title: "Validated Performance",
      showTitle: false,
      image: [{ cdn_url: DEFENDR_DRAFT_CONTENT.testing.image }],
      imageCover: true,
      text: {
        json: richTextDocument(
          paragraph(
            "Testing focuses on how the entire assembly performs under forced-entry conditions in real school settings.",
          ),
        ),
      },
      ctAsCollection: { items: [] },
    },
  };

  const resourcesBlock = {
    __typename: "Block",
    sys: { id: "defendr-resources-block" },
    title: DEFENDR_DRAFT_CONTENT.resources.title,
    showTitle: true,
    layout: "Accordion",
    padding: "Top and Bottom",
    anchor: "resources",
    accordionDetailCollection: {
      items: DEFENDR_DRAFT_CONTENT.resources.sections.map((section) => ({
        title: section.title,
        open: false,
        content: {
          json: richTextDocument(
            paragraph(
              `Open ${section.title.toLowerCase()} resources for specifications, literature, and planning support.`,
            ),
          ),
        },
        ctAsCollection: {
          items: section.items.map((item) => ({
            sys: {
              id: `${section.title}-${item.title}`
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-"),
            },
            title: item.title,
            link: item.href,
            type: "Button",
          })),
        },
      })),
    },
  };

  const caseStudiesBlock = {
    __typename: "Block",
    sys: { id: "defendr-case-studies-block" },
    title: "Case Studies",
    showTitle: true,
    layout: "Carousel",
    carouselType: "Card",
    padding: "Top and Bottom",
    anchor: "case-studies",
    carouselCollection: {
      items: DEFENDR_DRAFT_CONTENT.caseStudies.map((item) => ({
        sys: {
          id: `case-study-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        },
        title: item.title,
        image: [{ cdn_url: item.image }],
        imageCover: true,
        cardType: "Default",
        description: { json: richTextDocument(paragraph(item.description)) },
        link: item.href,
        linkText: "View Case Study",
      })),
    },
    ctAsCollection: { items: [] },
  };

  const ctaFormBlock = {
    __typename: "Block",
    sys: { id: "defendr-ebook-form-block" },
    title: DEFENDR_DRAFT_CONTENT.ctaForm.title,
    showTitle: true,
    layout: "E-book Form",
    padding: "Top and Bottom",
    anchor: DEFENDR_DRAFT_CONTENT.ctaForm.anchor,
    backgroundColor: "Black",
    image: [{ cdn_url: DEFENDR_DRAFT_CONTENT.ctaForm.image }],
    text: {
      json: richTextDocument(
        ...DEFENDR_DRAFT_CONTENT.ctaForm.paragraphs.map((copy) =>
          paragraph(copy),
        ),
      ),
    },
  };

  return {
    title: DEFENDR_DRAFT_CONTENT.title,
    handle: DEFENDR_HANDLE,
    pageType: DEFENDR_DRAFT_CONTENT.pageType,
    heroDescription: {
      json: richTextDocument(paragraph(DEFENDR_DRAFT_CONTENT.hero.description)),
    },
    heroBackground: {
      url:
        DEFENDR_DRAFT_CONTENT.hero.heroImage ||
        DEFENDR_DRAFT_CONTENT.hero.heroImageFallback,
      description: DEFENDR_DRAFT_CONTENT.hero.title,
      width: 1800,
      height: 1200,
    },
    heroImagePosition: DEFENDR_DRAFT_CONTENT.hero.heroImagePosition,
    blocksCollection: {
      items: [
        awardsBlock,
        introBlock,
        applicationsBlock,
        constructionBlock,
        tiersBlock,
        testingBlock,
        resourcesBlock,
        caseStudiesBlock,
        ctaFormBlock,
      ],
    },
  };
};

export const DEFENDR_MOCK_PAGE = buildDefendrMockPage();
